import { type CSSProperties, type JSX, useEffect, useState } from 'react';
import styles from './LoadingSpinner.module.scss';

/**
 * LoadingSpinnerSize defines the size variants for the loading spinner.
 * Following Figma design specifications:
 * - X-Large: 80px
 * - Large: 48px
 * - Medium: 24px
 * - Small: 16px
 */
export type LoadingSpinnerSize = 'X-Large' | 'Large' | 'Medium' | 'Small';

/**
 * LoadingSpinnerVariant defines the visual style variants for the loading spinner.
 */
export type LoadingSpinnerVariant = 'Spinner' | 'Gradient';

/**
 * LoadingSpinnerProps defines the props for the LoadingSpinner component.
 */
export interface LoadingSpinnerProps {
  /** Size variant following Figma design (X-Large, Large, Medium, Small) */
  size?: LoadingSpinnerSize;
  /** Visual style variant */
  type?: LoadingSpinnerVariant;
  /** Animation duration in milliseconds (min: 100ms, max: 10000ms) */
  animationDuration?: number;
  visible?: boolean;
}

/**
 * A customizable loading spinner component.
 *
 * Renders either a solid spinner or a gradient spinner in multiple sizes following Figma design.
 * You can control its animation speed.
 *
 * ## Import path
 * ```tsx
 * import { LoadingSpinner } from '@kpmg-global-audit-emu/react-ui-kit/components/LoadingSpinner';
 * ```
 * @param {LoadingSpinnerProps} props - Props for configuring the spinner.
 * @param {LoadingSpinnerSize} props.size - Defines the size of the spinner (X-Large, Large, Medium, Small). Default is `"Medium"`.
 * @param {LoadingSpinnerVariant} props.type - Defines the spinner style (solid or gradient). Default is `"Spinner"`.
 * @param {number} props.animationDuration - Duration of the spinner animation in milliseconds (100–10000). Default is `2000`.
 * @returns {JSX.Element} A loading spinner element.
 */
export const LoadingSpinner = ({
  size = 'Medium',
  type = 'Spinner',
  animationDuration = 2000,
  visible = true,
}: LoadingSpinnerProps): JSX.Element | null => {
  const [diameter, setDiameter] = useState(24); // Default medium size (24px)

  // Clamp animation duration between 100ms and 10000ms
  const duration = Math.min(Math.max(animationDuration, 100), 10000);

  // Update diameter based on size prop following Figma specifications
  useEffect(() => {
    switch (size) {
      case 'X-Large':
        setDiameter(80);
        break;
      case 'Large':
        setDiameter(48);
        break;
      case 'Medium':
        setDiameter(24);
        break;
      case 'Small':
        setDiameter(16);
        break;
      default:
        setDiameter(24);
    }
  }, [size]);

  const variantClass = type === 'Gradient' ? styles.gradient : styles.spinner;

  // Map size props to CSS classes
  const sizeClassMap: Record<LoadingSpinnerSize, string> = {
    'X-Large': styles.xLarge,
    Large: styles.large,
    Medium: styles.medium,
    Small: styles.small,
  };

  // Combine all classes
  const spinnerClasses = [styles.loadingSpinner, variantClass, sizeClassMap[size]]
    .filter(Boolean)
    .join(' ');

  if (!visible) return null;
  return (
    <div className={spinnerClasses} role="status" aria-label="Loading">
      <div
        className={styles.svgSpinnerContainer}
        style={{
          width: diameter,
          height: diameter,
        }}
      >
        <span
          aria-hidden="true"
          className={`${styles.spinnerRing} ${type === 'Gradient' ? styles.gradientRing : styles.solidRing
            }`}
          style={
            {
              width: diameter,
              height: diameter,
              ['--spinner-animation-duration' as string]: `${duration}ms`,
            } as CSSProperties
          }
        ></span>
      </div>
    </div>
  );
};
